/* ToneEngine — procedurally generates every theme sound in the browser using
 * the Web Audio API. No external audio files. Each `sound_id` maps to a
 * "recipe" that builds an audio graph; the engine owns playback (start /
 * stop / setVolume) so a parent UI can hot-swap sounds without leaks.
 *
 * Recipes intentionally stay simple: a couple of oscillators + filtered
 * noise is plenty to evoke solfeggio tones, AC hum, TV static, ocean
 * surf, heartbeats, etc. Anything more elaborate would need real audio
 * assets and we want zero hosting overhead.
 */

class ToneEngine {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.activeId = null;       // single-sound id, or "__mix__" when mixing
    this.layers = [];           // [{ id, gain, nodes: [] }] — one entry per stacked sound
    this.volume = 0.3;          // 0..1 master, persisted across swaps
  }

  _ensureCtx() {
    if (this.ctx) return this.ctx;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.volume;
    this.master.connect(this.ctx.destination);
    return this.ctx;
  }

  setVolume(v) {
    this.volume = Math.max(0, Math.min(1, Number(v) || 0));
    if (this.master) this.master.gain.value = this.volume;
  }

  isPlaying() {
    return !!this.activeId;
  }

  currentId() {
    return this.activeId;
  }

  /* Currently-playing mix as `[{id, volume}]` so the UI can stay in sync. */
  currentMix() {
    if (this.activeId !== "__mix__") return null;
    return this.layers.map((l) => ({ id: l.id, volume: l.gain ? l.gain.gain.value : 1 }));
  }

  /* Stop every node from every active layer. */
  stop() {
    for (const layer of this.layers) {
      for (const n of layer.nodes) {
        try { n.stop && n.stop(); } catch (e) { /* already stopped */ }
        try { n.disconnect && n.disconnect(); } catch (e) { /* already disconnected */ }
      }
      try { layer.gain && layer.gain.disconnect(); } catch (e) { /* ok */ }
    }
    this.layers = [];
    this.activeId = null;
  }

  /* Build a layer for `id`. Returns the layer record, or null if the
   * recipe is unknown. Caller pushes onto this.layers. */
  _buildLayer(ctx, id, layerVolume = 1.0) {
    const recipe = RECIPES[id];
    if (!recipe) return null;
    const gain = ctx.createGain();
    gain.gain.value = Math.max(0, Math.min(1, Number(layerVolume) || 0));
    gain.connect(this.master);
    const layer = { id, gain, nodes: [gain] };
    recipe(ctx, gain, (n) => layer.nodes.push(n));
    return layer;
  }

  /* Start a single sound by id. Returns true on success. */
  async play(id) {
    const ctx = this._ensureCtx();
    if (!ctx || !id) return false;
    if (this.activeId === id) return true;
    this.stop();
    try { await ctx.resume(); } catch (e) { /* ok */ }

    const layer = this._buildLayer(ctx, id, 1.0);
    if (!layer) return false;
    this.layers.push(layer);
    this.activeId = id;
    return true;
  }

  /* Start a stacked mix. `mix` = [{ id, volume }] — up to 3 layers.
   * Returns true if at least one valid layer was started. */
  async playMix(mix) {
    const ctx = this._ensureCtx();
    if (!ctx || !Array.isArray(mix) || mix.length === 0) return false;
    this.stop();
    try { await ctx.resume(); } catch (e) { /* ok */ }

    let any = false;
    for (const entry of mix.slice(0, 3)) {
      const layer = this._buildLayer(ctx, entry?.id, entry?.volume ?? 1.0);
      if (layer) {
        this.layers.push(layer);
        any = true;
      }
    }
    if (!any) return false;
    this.activeId = "__mix__";
    return true;
  }

  /* Set one layer's volume (0..1) without restarting playback. */
  setLayerVolume(layerIndex, v) {
    const layer = this.layers[layerIndex];
    if (!layer || !layer.gain) return;
    layer.gain.gain.value = Math.max(0, Math.min(1, Number(v) || 0));
  }
}

/* ---------- helpers ---------- */
function noiseBuffer(ctx, seconds, type = "white") {
  const length = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buf.getChannelData(0);
  if (type === "white") {
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  } else if (type === "pink") {
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < length; i++) {
      const w = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + w * 0.0555179;
      b1 = 0.99332 * b1 + w * 0.0750759;
      b2 = 0.96900 * b2 + w * 0.1538520;
      b3 = 0.86650 * b3 + w * 0.3104856;
      b4 = 0.55000 * b4 + w * 0.5329522;
      b5 = -0.7616 * b5 - w * 0.0168980;
      data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
      b6 = w * 0.115926;
    }
  } else if (type === "brown") {
    let last = 0;
    for (let i = 0; i < length; i++) {
      const w = Math.random() * 2 - 1;
      last = (last + 0.02 * w) / 1.02;
      data[i] = last * 3.5;
    }
  }
  return buf;
}

/* Pure sine drone with a gentle fade-in. */
function drone(ctx, master, push, freq, gain = 0.12, detune = 0) {
  const osc = ctx.createOscillator();
  osc.type = "sine";
  osc.frequency.value = freq;
  if (detune) osc.detune.value = detune;
  const g = ctx.createGain();
  g.gain.value = 0;
  g.gain.linearRampToValueAtTime(gain, ctx.currentTime + 0.8);
  osc.connect(g); g.connect(master);
  osc.start();
  push(osc); push(g);
  return { osc, gain: g };
}

function looper(ctx, master, push, type = "white", filterHz = null, gain = 0.18, bandwidthQ = 0.7) {
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx, 4, type);
  src.loop = true;
  const g = ctx.createGain();
  g.gain.value = gain;
  if (filterHz) {
    const f = ctx.createBiquadFilter();
    f.type = "bandpass";
    f.frequency.value = filterHz;
    f.Q.value = bandwidthQ;
    src.connect(f); f.connect(g); g.connect(master);
    push(f);
  } else {
    src.connect(g); g.connect(master);
  }
  src.start();
  push(src); push(g);
  return { src, gain: g };
}

/* Pulsed envelope — used for heartbeats, geiger clicks, sonar pings. */
function pulser(ctx, master, push, periodSec, attackFn) {
  let stopped = false;
  const tick = () => {
    if (stopped) return;
    attackFn(ctx.currentTime);
    const id = setTimeout(tick, periodSec * 1000);
    push({ stop: () => { stopped = true; clearTimeout(id); } });
  };
  tick();
}

/* ---------- recipes ---------- */
const RECIPES = {};

/* Solfeggio + Schumann — pure sines, slight binaural sparkle */
const solfBuilders = {
  solf_174: 174, solf_285: 285, solf_396: 396, solf_417: 417,
  solf_528: 528, solf_639: 639, solf_741: 741, solf_852: 852,
  solf_963: 963, schumann: 7.83,
};
for (const [id, hz] of Object.entries(solfBuilders)) {
  RECIPES[id] = (ctx, master, push) => {
    drone(ctx, master, push, hz, hz < 50 ? 0.18 : 0.10);
    if (hz >= 100) drone(ctx, master, push, hz, 0.04, 5); // gentle chorus
  };
}

/* Noise family */
RECIPES.white_static  = (c, m, p) => looper(c, m, p, "white", null, 0.10);
RECIPES.tv_static     = (c, m, p) => {
  looper(c, m, p, "white", 2200, 0.20, 0.3);
  looper(c, m, p, "white", 600, 0.10, 0.2);
};
RECIPES.pink_noise    = (c, m, p) => looper(c, m, p, "pink", null, 0.18);
RECIPES.brown_noise   = (c, m, p) => looper(c, m, p, "brown", null, 0.30);
RECIPES.ac_hum_60     = (c, m, p) => { drone(c, m, p, 60, 0.10); drone(c, m, p, 120, 0.06); };
RECIPES.ac_hum_50     = (c, m, p) => { drone(c, m, p, 50, 0.10); drone(c, m, p, 100, 0.06); };
RECIPES.fluorescent   = (c, m, p) => { drone(c, m, p, 120, 0.08); looper(c, m, p, "white", 5200, 0.05, 5); };
RECIPES.tape_hiss     = (c, m, p) => looper(c, m, p, "white", 4400, 0.07, 0.6);
RECIPES.vinyl_crackle = (c, m, p) => {
  looper(c, m, p, "white", 6000, 0.04, 0.8);
  pulser(c, m, p, 0.07 + Math.random() * 0.18, (t) => {
    const o = c.createOscillator(); o.type = "square"; o.frequency.value = 800 + Math.random() * 1200;
    const g = c.createGain(); g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.08, t + 0.001);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.04);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.05);
  });
};

/* Binaural beats — two close sines, one per ear */
function binaural(baseHz, beatHz) {
  return (ctx, master, push) => {
    const splitter = ctx.createChannelMerger(2);
    const oL = ctx.createOscillator(); oL.type = "sine"; oL.frequency.value = baseHz;
    const oR = ctx.createOscillator(); oR.type = "sine"; oR.frequency.value = baseHz + beatHz;
    const gL = ctx.createGain(); gL.gain.value = 0; gL.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 0.6);
    const gR = ctx.createGain(); gR.gain.value = 0; gR.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 0.6);
    oL.connect(gL); gL.connect(splitter, 0, 0);
    oR.connect(gR); gR.connect(splitter, 0, 1);
    splitter.connect(master);
    oL.start(); oR.start();
    [oL, oR, gL, gR, splitter].forEach(push);
  };
}
RECIPES.bin_delta = binaural(200, 2);
RECIPES.bin_theta = binaural(220, 6);
RECIPES.bin_alpha = binaural(240, 10);
RECIPES.bin_beta  = binaural(260, 18);
RECIPES.bin_gamma = binaural(280, 40);

/* Ambient */
RECIPES.amb_rain  = (c, m, p) => {
  looper(c, m, p, "pink", null, 0.25);
  // Distant thunder rumbles
  pulser(c, m, p, 5 + Math.random() * 8, (t) => {
    const src = c.createBufferSource(); src.buffer = noiseBuffer(c, 2, "brown");
    const f = c.createBiquadFilter(); f.type = "lowpass"; f.frequency.value = 200;
    const g = c.createGain(); g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.4, t + 0.4);
    g.gain.exponentialRampToValueAtTime(0.001, t + 2.0);
    src.connect(f); f.connect(g); g.connect(m); src.start(t); src.stop(t + 2.1);
  });
};
RECIPES.amb_cave  = (c, m, p) => { drone(c, m, p, 55, 0.12); drone(c, m, p, 82.5, 0.08); looper(c, m, p, "brown", null, 0.10); };
RECIPES.amb_wind  = (c, m, p) => looper(c, m, p, "pink", 800, 0.30, 0.4);
RECIPES.amb_heartbeat = (c, m, p) => {
  pulser(c, m, p, 1.0, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 60;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.45, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.2);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.22);
    // Second thump
    const t2 = t + 0.28;
    const o2 = c.createOscillator(); o2.type = "sine"; o2.frequency.value = 55;
    const g2 = c.createGain();
    g2.gain.setValueAtTime(0, t2);
    g2.gain.linearRampToValueAtTime(0.30, t2 + 0.02);
    g2.gain.exponentialRampToValueAtTime(0.0001, t2 + 0.22);
    o2.connect(g2); g2.connect(m); o2.start(t2); o2.stop(t2 + 0.24);
  });
};
RECIPES.amb_breath = (c, m, p) => {
  pulser(c, m, p, 5.5, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 5.5, "pink");
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 1200; f.Q.value = 0.7;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.25, t + 1.8);
    g.gain.linearRampToValueAtTime(0.10, t + 2.5);
    g.gain.linearRampToValueAtTime(0.18, t + 3.5);
    g.gain.linearRampToValueAtTime(0.0, t + 5.4);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 5.5);
  });
};
RECIPES.amb_ocean = (c, m, p) => {
  looper(c, m, p, "pink", 600, 0.20, 0.5);
  pulser(c, m, p, 4.2, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 5, "pink");
    const f = c.createBiquadFilter(); f.type = "lowpass"; f.frequency.value = 900;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.35, t + 2.0);
    g.gain.linearRampToValueAtTime(0.0, t + 5.0);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 5.1);
  });
};

/* Classified */
RECIPES.cls_numbers = (c, m, p) => {
  // Minor-chord drone (A2, C3, E3) + occasional female-voice-ish tone bursts
  drone(c, m, p, 110, 0.08); drone(c, m, p, 130.81, 0.07); drone(c, m, p, 164.81, 0.07);
  pulser(c, m, p, 4 + Math.random() * 3, (t) => {
    const tones = [440, 523, 587, 698, 880];
    const f = tones[Math.floor(Math.random() * tones.length)];
    const o = c.createOscillator(); o.type = "triangle"; o.frequency.value = f;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.12, t + 0.05);
    g.gain.linearRampToValueAtTime(0.0, t + 0.4);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.45);
  });
};
RECIPES.cls_sonar = (c, m, p) => {
  drone(c, m, p, 80, 0.05);
  pulser(c, m, p, 3.4, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.setValueAtTime(880, t);
    o.frequency.exponentialRampToValueAtTime(440, t + 1.2);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.25, t + 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.4);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 1.45);
  });
};
RECIPES.cls_geiger = (c, m, p) => {
  pulser(c, m, p, 0.15 + Math.random() * 0.55, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 0.05, "white");
    const f = c.createBiquadFilter(); f.type = "highpass"; f.frequency.value = 4000;
    const g = c.createGain();
    g.gain.setValueAtTime(0.4, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.04);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 0.05);
  });
};
RECIPES.cls_dialup = (c, m, p) => {
  /* Approximation of the famous handshake: a sequence of tones loops. */
  const seq = [2100, 1300, 2100, 1300, 980, 1180, 1650, 2100, 0, 800];
  let i = 0;
  const playOne = () => {
    const t = c.currentTime;
    const f = seq[i % seq.length];
    if (f > 0) {
      const o = c.createOscillator(); o.type = "sine"; o.frequency.value = f;
      const g = c.createGain();
      g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.18, t + 0.05);
      g.gain.linearRampToValueAtTime(0.0, t + 0.55);
      o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.6);
    } else {
      const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 0.6, "white");
      const fil = c.createBiquadFilter(); fil.type = "bandpass"; fil.frequency.value = 2500;
      const g = c.createGain(); g.gain.value = 0.08;
      s.connect(fil); fil.connect(g); g.connect(m); s.start(t); s.stop(t + 0.6);
    }
    i++;
    const id = setTimeout(playOne, 600);
    p({ stop: () => clearTimeout(id) });
  };
  playOne();
};
RECIPES.cls_morse = (c, m, p) => {
  /* Repeating "S O S" pattern: ... --- ... */
  const dots = [120, 120, 120, 360, 360, 360, 120, 120, 120, 0];
  let i = 0;
  const playOne = () => {
    const t = c.currentTime;
    const ms = dots[i % dots.length];
    if (ms > 0) {
      const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 660;
      const g = c.createGain();
      g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.15, t + 0.01);
      g.gain.linearRampToValueAtTime(0.0, t + ms / 1000 - 0.01);
      o.connect(g); g.connect(m); o.start(t); o.stop(t + ms / 1000);
    }
    i++;
    const id = setTimeout(playOne, (ms || 800) + 120);
    p({ stop: () => clearTimeout(id) });
  };
  playOne();
};
RECIPES.cls_tinnitus = (c, m, p) => { drone(c, m, p, 6800, 0.04); drone(c, m, p, 7050, 0.04); };
RECIPES.cls_clock = (c, m, p) => {
  pulser(c, m, p, 1.0, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 0.04, "white");
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 4500; f.Q.value = 8;
    const g = c.createGain();
    g.gain.setValueAtTime(0.25, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 0.08);
  });
};
RECIPES.cls_breath = (c, m, p) => {
  drone(c, m, p, 35, 0.08);
  pulser(c, m, p, 4, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 6, "pink");
    const f = c.createBiquadFilter(); f.type = "lowpass"; f.frequency.value = 700;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.22, t + 2.5);
    g.gain.linearRampToValueAtTime(0.0, t + 5.8);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 6.0);
  });
};

/* ---------- NATURE family (forests, fields, rivers) ---------------------- */
RECIPES.nat_forest_morning = (c, m, p) => {
  /* dawn forest — soft pink wind bed + a flock of randomly-pitched bird chirps */
  looper(c, m, p, "pink", 1800, 0.10, 0.6);
  pulser(c, m, p, 0.6 + Math.random() * 1.4, (t) => {
    const f0 = 1800 + Math.random() * 1800;
    const o = c.createOscillator(); o.type = "sine";
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(f0 * (0.65 + Math.random() * 0.7), t + 0.18);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.08, t + 0.04);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.25);
  });
};
RECIPES.nat_creek = (c, m, p) => {
  looper(c, m, p, "white", 2400, 0.07, 0.4);
  looper(c, m, p, "pink", 600, 0.18, 0.5);
};
RECIPES.nat_crickets = (c, m, p) => {
  pulser(c, m, p, 0.18 + Math.random() * 0.12, (t) => {
    const o = c.createOscillator(); o.type = "square"; o.frequency.value = 4400 + Math.random() * 700;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.05, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.04);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.05);
  });
};
RECIPES.nat_wind_trees = (c, m, p) => looper(c, m, p, "pink", 500, 0.28, 0.3);
RECIPES.nat_campfire = (c, m, p) => {
  looper(c, m, p, "brown", 800, 0.15, 0.5);
  pulser(c, m, p, 0.12 + Math.random() * 0.25, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 0.08, "white");
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 2200 + Math.random() * 1200; f.Q.value = 3;
    const g = c.createGain();
    g.gain.setValueAtTime(0.18, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.07);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 0.1);
  });
};
RECIPES.nat_owl = (c, m, p) => {
  drone(c, m, p, 55, 0.04);
  pulser(c, m, p, 4 + Math.random() * 3, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 360;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.12, t + 0.15);
    g.gain.linearRampToValueAtTime(0.0, t + 0.85);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.9);
    const t2 = t + 1.1;
    const o2 = c.createOscillator(); o2.type = "sine"; o2.frequency.value = 340;
    const g2 = c.createGain();
    g2.gain.setValueAtTime(0, t2); g2.gain.linearRampToValueAtTime(0.10, t2 + 0.12);
    g2.gain.linearRampToValueAtTime(0.0, t2 + 0.7);
    o2.connect(g2); g2.connect(m); o2.start(t2); o2.stop(t2 + 0.75);
  });
};
RECIPES.nat_whale = (c, m, p) => {
  drone(c, m, p, 70, 0.10);
  pulser(c, m, p, 6 + Math.random() * 5, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.setValueAtTime(220, t);
    o.frequency.exponentialRampToValueAtTime(70, t + 5.0);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.16, t + 1.0);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 5.0);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 5.1);
  });
};
RECIPES.nat_cicadas = (c, m, p) => looper(c, m, p, "white", 7200, 0.20, 12);
RECIPES.nat_meadow = (c, m, p) => {
  looper(c, m, p, "pink", 1100, 0.10, 0.4);
  RECIPES.nat_crickets(c, m, p);
};
RECIPES.nat_rain_window = (c, m, p) => {
  looper(c, m, p, "pink", 3200, 0.16, 0.4);
  looper(c, m, p, "brown", null, 0.10);
};

/* ---------- COZY family (indoor warmth) ---------------------------------- */
RECIPES.coz_fireplace = (c, m, p) => {
  drone(c, m, p, 45, 0.10);
  RECIPES.nat_campfire(c, m, p);
};
RECIPES.coz_cafe = (c, m, p) => {
  /* soft chatter bed: filtered pink noise + occasional clinks */
  looper(c, m, p, "pink", 600, 0.22, 0.6);
  pulser(c, m, p, 2 + Math.random() * 3, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 1700 + Math.random() * 800;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.08, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.4);
  });
};
RECIPES.coz_library = (c, m, p) => {
  drone(c, m, p, 60, 0.06); drone(c, m, p, 90, 0.04);
  looper(c, m, p, "pink", 3200, 0.04, 0.5);
};
RECIPES.coz_pages = (c, m, p) => {
  pulser(c, m, p, 4 + Math.random() * 4, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 0.45, "white");
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 1800; f.Q.value = 0.7;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.14, t + 0.1);
    g.gain.linearRampToValueAtTime(0.0, t + 0.45);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 0.5);
  });
};
RECIPES.coz_tea_kettle = (c, m, p) => {
  drone(c, m, p, 1850, 0.04);
  looper(c, m, p, "white", 2400, 0.10, 0.5);
};

/* ---------- SPACE family ------------------------------------------------- */
RECIPES.spc_deep = (c, m, p) => { drone(c, m, p, 42, 0.10); drone(c, m, p, 63, 0.08); drone(c, m, p, 84, 0.05); };
RECIPES.spc_wormhole = (c, m, p) => {
  drone(c, m, p, 90, 0.07);
  pulser(c, m, p, 0.5, (t) => {
    const o = c.createOscillator(); o.type = "sawtooth"; o.frequency.setValueAtTime(120, t);
    o.frequency.exponentialRampToValueAtTime(440, t + 0.45);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.05, t + 0.1);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.55);
  });
};
RECIPES.spc_solar_flare = (c, m, p) => {
  looper(c, m, p, "pink", 4400, 0.08, 0.6);
  pulser(c, m, p, 2.2, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 0.6, "white");
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 2200; f.Q.value = 1.2;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.22, t + 0.3);
    g.gain.linearRampToValueAtTime(0.0, t + 0.6);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 0.65);
  });
};
RECIPES.spc_pulsar = (c, m, p) => {
  drone(c, m, p, 50, 0.06);
  pulser(c, m, p, 1.337, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 0.06, "white");
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 3600; f.Q.value = 6;
    const g = c.createGain();
    g.gain.setValueAtTime(0.32, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 0.07);
  });
};
RECIPES.spc_nebula = (c, m, p) => {
  /* root + maj7 + 5 = lush */
  drone(c, m, p, 110, 0.08);
  drone(c, m, p, 138.59, 0.06);
  drone(c, m, p, 164.81, 0.06);
  drone(c, m, p, 207.65, 0.05);
};
RECIPES.spc_starfield = (c, m, p) => {
  drone(c, m, p, 90, 0.06);
  pulser(c, m, p, 0.4 + Math.random() * 0.8, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 2200 + Math.random() * 2400;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.04, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.32);
  });
};

/* ---------- RELAXING family (bells, chimes, drones) --------------------- */
RECIPES.rel_singing_bowl = (c, m, p) => {
  pulser(c, m, p, 5.0, (t) => {
    const fundamentals = [196, 220, 247, 277, 311];
    const f = fundamentals[Math.floor(Math.random() * fundamentals.length)];
    [1, 2.78, 5.5].forEach((mult, i) => {
      const o = c.createOscillator(); o.type = "sine"; o.frequency.value = f * mult;
      const g = c.createGain();
      g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.10 / (i + 1), t + 0.1);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 6.5);
      o.connect(g); g.connect(m); o.start(t); o.stop(t + 6.7);
    });
  });
};
RECIPES.rel_wind_chimes = (c, m, p) => {
  pulser(c, m, p, 2 + Math.random() * 3, (t) => {
    const tones = [523, 587, 659, 698, 784, 880, 988];
    const f = tones[Math.floor(Math.random() * tones.length)];
    const o = c.createOscillator(); o.type = "triangle"; o.frequency.value = f;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.10, t + 0.04);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.8);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 1.85);
  });
};
RECIPES.rel_choir_pad = (c, m, p) => {
  /* low choir-like pad */
  drone(c, m, p, 130.81, 0.07);
  drone(c, m, p, 196.00, 0.06, 5);
  drone(c, m, p, 246.94, 0.05, -3);
};
RECIPES.rel_bamboo_flute = (c, m, p) => {
  pulser(c, m, p, 3 + Math.random() * 2, (t) => {
    const f = [440, 494, 523, 587, 659][Math.floor(Math.random() * 5)];
    const o = c.createOscillator(); o.type = "sine"; o.frequency.value = f;
    const breath = c.createBufferSource(); breath.buffer = noiseBuffer(c, 2.5, "pink");
    const bf = c.createBiquadFilter(); bf.type = "bandpass"; bf.frequency.value = f; bf.Q.value = 2;
    const bg = c.createGain(); bg.gain.value = 0.05;
    breath.connect(bf); bf.connect(bg); bg.connect(m);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.14, t + 0.4);
    g.gain.linearRampToValueAtTime(0.0, t + 2.4);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 2.5);
    breath.start(t); breath.stop(t + 2.5);
  });
};
RECIPES.rel_crystal_pad = (c, m, p) => {
  drone(c, m, p, 261.63, 0.05);
  drone(c, m, p, 329.63, 0.05);
  drone(c, m, p, 392.00, 0.05);
  drone(c, m, p, 523.25, 0.04, 4);
};
RECIPES.rel_om = (c, m, p) => {
  drone(c, m, p, 136.1, 0.14); // OM frequency
  drone(c, m, p, 272.2, 0.05, 3);
};
RECIPES.rel_zen_garden = (c, m, p) => {
  looper(c, m, p, "pink", 800, 0.10, 0.5);
  pulser(c, m, p, 4 + Math.random() * 3, (t) => {
    // a single soft tone like a koto pluck
    const f = [220, 247, 277, 330, 370][Math.floor(Math.random() * 5)];
    const o = c.createOscillator(); o.type = "triangle"; o.frequency.value = f;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.10, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 2.2);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 2.25);
  });
};

/* ---------- More NATURE ------------------------------------------------- */
RECIPES.nat_ocean_waves = (c, m, p) => {
  /* slow swell + foam burst — uses pink noise modulated by a 0.1 Hz LFO */
  const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 16, "pink"); s.loop = true;
  const f = c.createBiquadFilter(); f.type = "lowpass"; f.frequency.value = 1200;
  const g = c.createGain(); g.gain.value = 0;
  const lfo = c.createOscillator(); lfo.frequency.value = 0.10;
  const lfoG = c.createGain(); lfoG.gain.value = 0.22;
  lfo.connect(lfoG); lfoG.connect(g.gain);
  s.connect(f); f.connect(g); g.connect(m);
  s.start(); lfo.start();
  p(s); p(lfo); p(lfoG); p(g); p(f);
};
RECIPES.nat_light_rain = (c, m, p) => {
  looper(c, m, p, "pink", 4000, 0.10, 0.45);
  pulser(c, m, p, 0.03 + Math.random() * 0.04, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 0.03, "white");
    const g = c.createGain();
    g.gain.setValueAtTime(0.04, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.03);
    s.connect(g); g.connect(m); s.start(t); s.stop(t + 0.04);
  });
};
RECIPES.nat_thunderstorm = (c, m, p) => {
  looper(c, m, p, "brown", 800, 0.18, 0.5);
  pulser(c, m, p, 10 + Math.random() * 14, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 4, "brown");
    const f = c.createBiquadFilter(); f.type = "lowpass"; f.frequency.value = 120;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.45, t + 0.4);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 3.8);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 4);
  });
};
RECIPES.nat_distant_thunder = (c, m, p) => looper(c, m, p, "brown", 110, 0.18, 0.4);
RECIPES.nat_spring_peepers = (c, m, p) => {
  pulser(c, m, p, 0.2 + Math.random() * 0.18, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 2800 + Math.random() * 600;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.07, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.13);
  });
};
RECIPES.nat_wolves = (c, m, p) => {
  drone(c, m, p, 50, 0.04);
  pulser(c, m, p, 6 + Math.random() * 7, (t) => {
    const o = c.createOscillator(); o.type = "sawtooth"; o.frequency.setValueAtTime(180, t);
    o.frequency.linearRampToValueAtTime(440, t + 0.6); o.frequency.linearRampToValueAtTime(310, t + 4.0);
    const f = c.createBiquadFilter(); f.type = "lowpass"; f.frequency.value = 1200;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.14, t + 0.5);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 4.2);
    o.connect(f); f.connect(g); g.connect(m); o.start(t); o.stop(t + 4.3);
  });
};
RECIPES.nat_loon = (c, m, p) => {
  drone(c, m, p, 60, 0.04);
  pulser(c, m, p, 7 + Math.random() * 5, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.setValueAtTime(660, t);
    o.frequency.linearRampToValueAtTime(880, t + 0.3); o.frequency.linearRampToValueAtTime(440, t + 1.4);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.12, t + 0.1);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.5);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 1.55);
  });
};
RECIPES.nat_grass_wind = (c, m, p) => looper(c, m, p, "pink", 220, 0.22, 0.4);
RECIPES.nat_coyotes = (c, m, p) => {
  pulser(c, m, p, 9 + Math.random() * 11, (t) => {
    for (let i = 0; i < 3; i++) {
      const o = c.createOscillator(); o.type = "sawtooth"; o.frequency.value = 600 + i * 80;
      const f = c.createBiquadFilter(); f.type = "lowpass"; f.frequency.value = 1400;
      const g = c.createGain();
      g.gain.setValueAtTime(0, t + i * 0.2); g.gain.linearRampToValueAtTime(0.06, t + i * 0.2 + 0.05);
      g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.2 + 0.4);
      o.connect(f); f.connect(g); g.connect(m); o.start(t + i * 0.2); o.stop(t + i * 0.2 + 0.45);
    }
  });
};
RECIPES.nat_heavy_rain = (c, m, p) => {
  looper(c, m, p, "white", 4400, 0.30, 0.4);
  looper(c, m, p, "brown", 600, 0.20, 0.6);
};

/* ---------- More COZY -------------------------------------------------- */
RECIPES.coz_vinyl_crackle = (c, m, p) => {
  pulser(c, m, p, 0.06 + Math.random() * 0.04, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 0.012, "white");
    const f = c.createBiquadFilter(); f.type = "highpass"; f.frequency.value = 3000;
    const g = c.createGain();
    g.gain.setValueAtTime(0.05 + Math.random() * 0.04, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.014);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 0.02);
  });
};
RECIPES.coz_cassette_hiss = (c, m, p) => looper(c, m, p, "pink", 6800, 0.06, 0.4);
RECIPES.coz_clock_tick = (c, m, p) => {
  pulser(c, m, p, 1.0, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 0.04, "white");
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 4400; f.Q.value = 5;
    const g = c.createGain();
    g.gain.setValueAtTime(0.18, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.04);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 0.05);
  });
};
RECIPES.coz_percolator = (c, m, p) => {
  looper(c, m, p, "pink", 1400, 0.06, 0.6);
  pulser(c, m, p, 0.3 + Math.random() * 0.25, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 280 + Math.random() * 120;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.06, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.14);
  });
};
RECIPES.coz_rocking_chair = (c, m, p) => {
  pulser(c, m, p, 1.6, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 0.6, "brown");
    const f = c.createBiquadFilter(); f.type = "lowpass"; f.frequency.value = 320;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.10, t + 0.3);
    g.gain.linearRampToValueAtTime(0.0, t + 0.85);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 0.9);
  });
};
RECIPES.coz_distant_train = (c, m, p) => {
  drone(c, m, p, 60, 0.10);
  pulser(c, m, p, 18 + Math.random() * 22, (t) => {
    const o1 = c.createOscillator(); o1.type = "sine"; o1.frequency.value = 220;
    const o2 = c.createOscillator(); o2.type = "sine"; o2.frequency.value = 277;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.12, t + 0.5);
    g.gain.linearRampToValueAtTime(0.0, t + 3.0);
    o1.connect(g); o2.connect(g); g.connect(m);
    o1.start(t); o2.start(t); o1.stop(t + 3.1); o2.stop(t + 3.1);
  });
};
RECIPES.coz_floor_creak = (c, m, p) => {
  pulser(c, m, p, 9 + Math.random() * 9, (t) => {
    const o = c.createOscillator(); o.type = "sawtooth"; o.frequency.setValueAtTime(120, t);
    o.frequency.exponentialRampToValueAtTime(70, t + 0.5);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.05, t + 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.55);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.6);
  });
};

/* ---------- More SPACE ------------------------------------------------- */
RECIPES.spc_black_hole = (c, m, p) => { drone(c, m, p, 28, 0.18); drone(c, m, p, 42, 0.08, 7); };
RECIPES.spc_solar_wind = (c, m, p) => looper(c, m, p, "pink", 320, 0.20, 0.5);
RECIPES.spc_voyager = (c, m, p) => {
  drone(c, m, p, 110, 0.06); drone(c, m, p, 164.81, 0.05);
  pulser(c, m, p, 6 + Math.random() * 5, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 880 + Math.random() * 880;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.04, t + 0.1);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.0);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 1.05);
  });
};
RECIPES.spc_tesla_arc = (c, m, p) => {
  pulser(c, m, p, 0.15 + Math.random() * 0.25, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 0.06, "white");
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 6000 + Math.random() * 1800; f.Q.value = 4;
    const g = c.createGain();
    g.gain.setValueAtTime(0.18, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 0.08);
  });
};
RECIPES.spc_iss_hum = (c, m, p) => { drone(c, m, p, 80, 0.12); drone(c, m, p, 240, 0.04, 4); };

/* ---------- More RELAXING / SACRED ------------------------------------- */
RECIPES.rel_heartbeat = (c, m, p) => {
  /* 60 BPM lub-dub */
  pulser(c, m, p, 1.0, (t) => {
    [{ ofs: 0, vol: 0.18 }, { ofs: 0.28, vol: 0.14 }].forEach(({ ofs, vol }) => {
      const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 70;
      const g = c.createGain();
      g.gain.setValueAtTime(0, t + ofs); g.gain.linearRampToValueAtTime(vol, t + ofs + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + ofs + 0.18);
      o.connect(g); g.connect(m); o.start(t + ofs); o.stop(t + ofs + 0.2);
    });
  });
};
RECIPES.rel_hang_drum = (c, m, p) => {
  pulser(c, m, p, 2 + Math.random() * 1.5, (t) => {
    const notes = [196, 220, 261.63, 293.66, 329.63, 392];
    const f = notes[Math.floor(Math.random() * notes.length)];
    [1, 2, 3].forEach((mult, i) => {
      const o = c.createOscillator(); o.type = "sine"; o.frequency.value = f * mult;
      const g = c.createGain();
      g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.10 / (i + 1), t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 3.5);
      o.connect(g); g.connect(m); o.start(t); o.stop(t + 3.6);
    });
  });
};
RECIPES.rel_rain_stick = (c, m, p) => {
  pulser(c, m, p, 5 + Math.random() * 4, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 6, "white");
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.setValueAtTime(2400, t);
    f.frequency.exponentialRampToValueAtTime(800, t + 5); f.Q.value = 1.5;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.18, t + 0.3);
    g.gain.linearRampToValueAtTime(0.0, t + 5);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 5.1);
  });
};
RECIPES.rel_gong = (c, m, p) => {
  pulser(c, m, p, 10, (t) => {
    [80, 120, 200, 280, 410].forEach((f, i) => {
      const o = c.createOscillator(); o.type = "sine"; o.frequency.value = f * (1 + Math.random() * 0.02);
      const g = c.createGain();
      g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.10 / (i + 1), t + 0.05);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 10);
      o.connect(g); g.connect(m); o.start(t); o.stop(t + 10.2);
    });
  });
};
RECIPES.rel_theremin_pad = (c, m, p) => {
  const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 330;
  const lfo = c.createOscillator(); lfo.frequency.value = 0.18;
  const lfoG = c.createGain(); lfoG.gain.value = 30;
  lfo.connect(lfoG); lfoG.connect(o.frequency);
  const g = c.createGain(); g.gain.value = 0.10;
  o.connect(g); g.connect(m); o.start(); lfo.start();
  p(o); p(lfo); p(lfoG); p(g);
};
RECIPES.rel_schumann = (c, m, p) => {
  /* 7.83 Hz isn't audible — we use a binaural beat: 110 + 117.83 */
  drone(c, m, p, 110, 0.08);
  drone(c, m, p, 117.83, 0.08);
};
RECIPES.rel_432hz = (c, m, p) => drone(c, m, p, 432, 0.10);
RECIPES.rel_528hz = (c, m, p) => drone(c, m, p, 528, 0.10);
RECIPES.rel_396hz = (c, m, p) => drone(c, m, p, 396, 0.10);
RECIPES.rel_639hz = (c, m, p) => drone(c, m, p, 639, 0.10);

/* ---------- UNDERWATER family ------------------------------------------- */
RECIPES.uw_submarine = (c, m, p) => { drone(c, m, p, 40, 0.18); drone(c, m, p, 80, 0.05, 4); };
RECIPES.uw_sonar = (c, m, p) => {
  drone(c, m, p, 55, 0.06);
  pulser(c, m, p, 3.0, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 1100;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.16, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.8);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.82);
  });
};
RECIPES.uw_bubbles = (c, m, p) => {
  pulser(c, m, p, 0.3 + Math.random() * 0.5, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.setValueAtTime(600 + Math.random() * 600, t);
    o.frequency.exponentialRampToValueAtTime(200, t + 0.18);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.07, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.2);
  });
};
RECIPES.uw_coral_reef = (c, m, p) => { looper(c, m, p, "brown", 600, 0.10, 0.5); RECIPES.uw_bubbles(c, m, p); };

/* ============================================================
 * BIG EXPANSION — ~100 additional ambient sounds. These all use
 * the existing helpers (drone / looper / pulser / noiseBuffer)
 * so they remain cheap, leak-free, and ready to be stacked by
 * the Sound Mixer.
 * ============================================================ */

/* ---------- More NATURE (15) ---------------------------------- */
RECIPES.nat_swamp = (c, m, p) => {
  looper(c, m, p, "pink", 380, 0.18, 0.4);
  pulser(c, m, p, 0.4 + Math.random() * 0.6, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 90 + Math.random() * 50;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.10, t + 0.04);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.2);
  });
};
RECIPES.nat_jungle = (c, m, p) => {
  looper(c, m, p, "pink", 1400, 0.10, 0.5);
  RECIPES.nat_crickets(c, m, p);
  pulser(c, m, p, 3 + Math.random() * 4, (t) => {
    const f = 1400 + Math.random() * 1600;
    const o = c.createOscillator(); o.type = "triangle";
    o.frequency.setValueAtTime(f, t);
    o.frequency.exponentialRampToValueAtTime(f * 1.4, t + 0.25);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.10, t + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.32);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.35);
  });
};
RECIPES.nat_rapids = (c, m, p) => {
  looper(c, m, p, "white", 1800, 0.22, 0.4);
  looper(c, m, p, "brown", 200, 0.18, 0.5);
};
RECIPES.nat_glacier = (c, m, p) => {
  drone(c, m, p, 38, 0.18); drone(c, m, p, 57, 0.05, 4);
  pulser(c, m, p, 11 + Math.random() * 11, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 2.5, "brown");
    const f = c.createBiquadFilter(); f.type = "lowpass"; f.frequency.value = 90;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.30, t + 0.3);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 2.4);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 2.5);
  });
};
RECIPES.nat_geyser = (c, m, p) => {
  looper(c, m, p, "white", 2200, 0.18, 0.4);
  pulser(c, m, p, 7 + Math.random() * 6, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 4, "white");
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 1800; f.Q.value = 0.8;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.45, t + 0.6);
    g.gain.linearRampToValueAtTime(0.0, t + 3.8);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 4);
  });
};
RECIPES.nat_mountain_stream = (c, m, p) => {
  looper(c, m, p, "white", 3200, 0.12, 0.45);
  looper(c, m, p, "pink", 900, 0.14, 0.5);
};
RECIPES.nat_lake = (c, m, p) => {
  looper(c, m, p, "pink", 400, 0.08, 0.5);
  pulser(c, m, p, 4 + Math.random() * 3, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 1.8, "pink");
    const f = c.createBiquadFilter(); f.type = "lowpass"; f.frequency.value = 700;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.16, t + 0.8);
    g.gain.linearRampToValueAtTime(0.0, t + 1.7);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 1.8);
  });
};
RECIPES.nat_gulls = (c, m, p) => {
  drone(c, m, p, 60, 0.04);
  pulser(c, m, p, 2.5 + Math.random() * 2.5, (t) => {
    const o = c.createOscillator(); o.type = "sawtooth"; o.frequency.value = 800 + Math.random() * 600;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.06, t + 0.06);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.32);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.35);
  });
};
RECIPES.nat_mosquito = (c, m, p) => {
  const o = c.createOscillator(); o.type = "sawtooth"; o.frequency.value = 1450;
  const lfo = c.createOscillator(); lfo.frequency.value = 5.5;
  const lfoG = c.createGain(); lfoG.gain.value = 80;
  lfo.connect(lfoG); lfoG.connect(o.frequency);
  const g = c.createGain(); g.gain.value = 0.04;
  o.connect(g); g.connect(m); o.start(); lfo.start();
  p(o); p(lfo); p(lfoG); p(g);
};
RECIPES.nat_leaves_rustle = (c, m, p) => looper(c, m, p, "pink", 3600, 0.20, 0.4);
RECIPES.nat_snow_crunch = (c, m, p) => {
  pulser(c, m, p, 0.45, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 0.16, "white");
    const f = c.createBiquadFilter(); f.type = "highpass"; f.frequency.value = 2200;
    const g = c.createGain();
    g.gain.setValueAtTime(0.20, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.14);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 0.16);
  });
};
RECIPES.nat_blizzard = (c, m, p) => {
  looper(c, m, p, "white", 1100, 0.34, 0.5);
  looper(c, m, p, "pink", 300, 0.20, 0.4);
};
RECIPES.nat_dragonfly = (c, m, p) => {
  pulser(c, m, p, 4 + Math.random() * 6, (t) => {
    const o = c.createOscillator(); o.type = "sawtooth"; o.frequency.value = 280;
    const lfo = c.createOscillator(); lfo.frequency.value = 18;
    const lfoG = c.createGain(); lfoG.gain.value = 40;
    lfo.connect(lfoG); lfoG.connect(o.frequency);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.06, t + 0.1);
    g.gain.linearRampToValueAtTime(0.0, t + 1.4);
    o.connect(g); g.connect(m);
    o.start(t); lfo.start(t); o.stop(t + 1.5); lfo.stop(t + 1.5);
  });
};
RECIPES.nat_frog_chorus = (c, m, p) => {
  pulser(c, m, p, 0.5 + Math.random() * 0.6, (t) => {
    const o = c.createOscillator(); o.type = "sawtooth"; o.frequency.value = 200 + Math.random() * 60;
    const f = c.createBiquadFilter(); f.type = "lowpass"; f.frequency.value = 600;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.10, t + 0.04);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
    o.connect(f); f.connect(g); g.connect(m); o.start(t); o.stop(t + 0.25);
  });
};
RECIPES.nat_dawn_chorus = (c, m, p) => {
  looper(c, m, p, "pink", 2000, 0.08, 0.5);
  pulser(c, m, p, 0.35 + Math.random() * 0.5, (t) => {
    const f0 = 2200 + Math.random() * 2400;
    const o = c.createOscillator(); o.type = "triangle";
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(f0 * (0.6 + Math.random()), t + 0.16);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.07, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.2);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.22);
  });
};

/* ---------- More COZY (12) ----------------------------------- */
RECIPES.coz_washer = (c, m, p) => {
  drone(c, m, p, 70, 0.12);
  const lfo = c.createOscillator(); lfo.frequency.value = 0.4;
  const lfoG = c.createGain(); lfoG.gain.value = 12;
  const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 70;
  lfo.connect(lfoG); lfoG.connect(o.frequency);
  const g = c.createGain(); g.gain.value = 0.08;
  o.connect(g); g.connect(m); o.start(); lfo.start();
  p(o); p(lfo); p(lfoG); p(g);
};
RECIPES.coz_dryer = (c, m, p) => {
  looper(c, m, p, "brown", 280, 0.22, 0.5);
  pulser(c, m, p, 0.7, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 0.05, "white");
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 1800; f.Q.value = 4;
    const g = c.createGain();
    g.gain.setValueAtTime(0.10, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 0.06);
  });
};
RECIPES.coz_dishwasher = (c, m, p) => looper(c, m, p, "white", 2400, 0.18, 0.4);
RECIPES.coz_fridge_hum = (c, m, p) => { drone(c, m, p, 110, 0.12); drone(c, m, p, 220, 0.04, 6); };
RECIPES.coz_ceiling_fan = (c, m, p) => {
  drone(c, m, p, 60, 0.08);
  const lfo = c.createOscillator(); lfo.frequency.value = 4.5;
  const lfoG = c.createGain(); lfoG.gain.value = 0.06;
  const baseG = c.createGain(); baseG.gain.value = 0.1;
  const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 4, "pink"); s.loop = true;
  lfo.connect(lfoG); lfoG.connect(baseG.gain);
  s.connect(baseG); baseG.connect(m);
  s.start(); lfo.start();
  p(s); p(lfo); p(lfoG); p(baseG);
};
RECIPES.coz_radiator = (c, m, p) => {
  drone(c, m, p, 45, 0.10);
  pulser(c, m, p, 5 + Math.random() * 6, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 0.1, "white");
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 3200; f.Q.value = 8;
    const g = c.createGain();
    g.gain.setValueAtTime(0.18, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 0.14);
  });
};
RECIPES.coz_microwave = (c, m, p) => { drone(c, m, p, 60, 0.06); looper(c, m, p, "white", 2800, 0.06, 0.4); };
RECIPES.coz_oven_preheat = (c, m, p) => { drone(c, m, p, 50, 0.10); looper(c, m, p, "brown", 400, 0.10, 0.5); };
RECIPES.coz_ice_clink = (c, m, p) => {
  pulser(c, m, p, 3 + Math.random() * 4, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 2200 + Math.random() * 800;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.10, t + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.55);
  });
};
RECIPES.coz_typing = (c, m, p) => {
  pulser(c, m, p, 0.15 + Math.random() * 0.2, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 0.03, "white");
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 2800; f.Q.value = 5;
    const g = c.createGain();
    g.gain.setValueAtTime(0.16, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.03);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 0.04);
  });
};
RECIPES.coz_sewing = (c, m, p) => {
  pulser(c, m, p, 0.12, (t) => {
    const o = c.createOscillator(); o.type = "square"; o.frequency.value = 240;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.05, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.04);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.05);
  });
};
RECIPES.coz_dog_breathing = (c, m, p) => {
  pulser(c, m, p, 1.8, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 1.4, "pink");
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 800; f.Q.value = 1.2;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.12, t + 0.4);
    g.gain.linearRampToValueAtTime(0.0, t + 1.3);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 1.4);
  });
};

/* ---------- More SPACE (10) ---------------------------------- */
RECIPES.spc_satellite_beep = (c, m, p) => {
  drone(c, m, p, 90, 0.04);
  pulser(c, m, p, 2.0, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 1200;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.18, t + 0.005);
    g.gain.linearRampToValueAtTime(0.0, t + 0.18);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.2);
  });
};
RECIPES.spc_comet_tail = (c, m, p) => {
  drone(c, m, p, 110, 0.06);
  looper(c, m, p, "white", 5400, 0.12, 0.4);
};
RECIPES.spc_radiation = (c, m, p) => {
  pulser(c, m, p, 0.08 + Math.random() * 0.18, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 0.02, "white");
    const f = c.createBiquadFilter(); f.type = "highpass"; f.frequency.value = 5000;
    const g = c.createGain();
    g.gain.setValueAtTime(0.30, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.02);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 0.03);
  });
};
RECIPES.spc_gravity_well = (c, m, p) => {
  drone(c, m, p, 30, 0.20); drone(c, m, p, 38, 0.10, 3);
  const lfo = c.createOscillator(); lfo.frequency.value = 0.07;
  const lfoG = c.createGain(); lfoG.gain.value = 8;
  const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 60;
  lfo.connect(lfoG); lfoG.connect(o.frequency);
  const g = c.createGain(); g.gain.value = 0.08;
  o.connect(g); g.connect(m); o.start(); lfo.start();
  p(o); p(lfo); p(lfoG); p(g);
};
RECIPES.spc_mars_wind = (c, m, p) => looper(c, m, p, "pink", 1400, 0.28, 0.45);
RECIPES.spc_lunar_silence = (c, m, p) => { drone(c, m, p, 25, 0.05); drone(c, m, p, 38, 0.03, 7); };
RECIPES.spc_time_dilation = (c, m, p) => {
  drone(c, m, p, 65, 0.05);
  const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 220;
  const lfo = c.createOscillator(); lfo.frequency.value = 0.03;
  const lfoG = c.createGain(); lfoG.gain.value = 50;
  lfo.connect(lfoG); lfoG.connect(o.frequency);
  const g = c.createGain(); g.gain.value = 0.06;
  o.connect(g); g.connect(m); o.start(); lfo.start();
  p(o); p(lfo); p(lfoG); p(g);
};
RECIPES.spc_plasma_field = (c, m, p) => {
  looper(c, m, p, "white", 1200, 0.10, 0.4);
  looper(c, m, p, "pink", 4400, 0.08, 0.5);
};
RECIPES.spc_ion_drive = (c, m, p) => { drone(c, m, p, 60, 0.10); drone(c, m, p, 120, 0.05, 4); drone(c, m, p, 240, 0.03, 8); };
RECIPES.spc_alien_morse = (c, m, p) => {
  drone(c, m, p, 45, 0.04);
  pulser(c, m, p, 0.4 + Math.random() * 0.5, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 1800 + Math.random() * 1200;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.12, t + 0.005);
    g.gain.linearRampToValueAtTime(0.0, t + 0.06 + Math.random() * 0.14);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.25);
  });
};

/* ---------- More RELAXING (12) ------------------------------- */
RECIPES.rel_cathedral_bells = (c, m, p) => {
  pulser(c, m, p, 5 + Math.random() * 2, (t) => {
    [262, 330, 392].forEach((f, i) => {
      const o = c.createOscillator(); o.type = "sine"; o.frequency.value = f;
      const g = c.createGain();
      g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.10 / (i + 1), t + 0.04);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 5);
      o.connect(g); g.connect(m); o.start(t); o.stop(t + 5.2);
    });
  });
};
RECIPES.rel_monk_chant = (c, m, p) => {
  drone(c, m, p, 110, 0.10); drone(c, m, p, 165, 0.06, 3); drone(c, m, p, 220, 0.04, -2);
};
RECIPES.rel_harp = (c, m, p) => {
  pulser(c, m, p, 3 + Math.random() * 2, (t) => {
    const notes = [261.63, 329.63, 392.00, 493.88, 523.25, 659.25];
    notes.forEach((f, i) => {
      const o = c.createOscillator(); o.type = "triangle"; o.frequency.value = f;
      const g = c.createGain();
      const at = t + i * 0.18;
      g.gain.setValueAtTime(0, at); g.gain.linearRampToValueAtTime(0.06, at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 1.6);
      o.connect(g); g.connect(m); o.start(at); o.stop(at + 1.7);
    });
  });
};
RECIPES.rel_music_box = (c, m, p) => {
  const seq = [523, 659, 783, 659, 523, 587, 523, 440];
  let i = 0;
  const playOne = () => {
    const t = c.currentTime;
    const f = seq[i % seq.length];
    const o = c.createOscillator(); o.type = "sine"; o.frequency.value = f;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.08, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.2);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 1.25);
    i++;
    const id = setTimeout(playOne, 600);
    p({ stop: () => clearTimeout(id) });
  };
  playOne();
};
RECIPES.rel_lullaby = (c, m, p) => {
  drone(c, m, p, 196, 0.05); drone(c, m, p, 247, 0.04);
  RECIPES.rel_music_box(c, m, p);
};
RECIPES.rel_kalimba = (c, m, p) => {
  pulser(c, m, p, 1.6 + Math.random() * 1.4, (t) => {
    const tones = [392, 440, 523, 587, 659, 784];
    const f = tones[Math.floor(Math.random() * tones.length)];
    const o = c.createOscillator(); o.type = "sine"; o.frequency.value = f;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.10, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.95);
  });
};
RECIPES.rel_koto = (c, m, p) => {
  pulser(c, m, p, 2 + Math.random() * 1.5, (t) => {
    const tones = [220, 247, 277, 330, 370, 440];
    const f = tones[Math.floor(Math.random() * tones.length)];
    const o = c.createOscillator(); o.type = "triangle"; o.frequency.value = f;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.12, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 2.6);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 2.7);
  });
};
RECIPES.rel_sitar_drone = (c, m, p) => {
  drone(c, m, p, 110, 0.10); drone(c, m, p, 220, 0.05, 8); drone(c, m, p, 330, 0.04, -5);
};
RECIPES.rel_didgeridoo = (c, m, p) => {
  const o = c.createOscillator(); o.type = "sawtooth"; o.frequency.value = 73;
  const lfo = c.createOscillator(); lfo.frequency.value = 0.6;
  const lfoG = c.createGain(); lfoG.gain.value = 4;
  lfo.connect(lfoG); lfoG.connect(o.frequency);
  const f = c.createBiquadFilter(); f.type = "lowpass"; f.frequency.value = 800;
  const g = c.createGain(); g.gain.value = 0.10;
  o.connect(f); f.connect(g); g.connect(m); o.start(); lfo.start();
  p(o); p(lfo); p(lfoG); p(f); p(g);
};
RECIPES.rel_panflute = (c, m, p) => {
  pulser(c, m, p, 3.5 + Math.random() * 2.5, (t) => {
    const f = [392, 440, 523, 587, 659][Math.floor(Math.random() * 5)];
    const o = c.createOscillator(); o.type = "sine"; o.frequency.value = f;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.10, t + 0.25);
    g.gain.linearRampToValueAtTime(0.0, t + 1.6);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 1.7);
  });
};
RECIPES.rel_marimba = (c, m, p) => {
  pulser(c, m, p, 1.2 + Math.random() * 0.8, (t) => {
    const tones = [196, 220, 261.63, 330, 392];
    const f = tones[Math.floor(Math.random() * tones.length)];
    [1, 2.3].forEach((mult, i) => {
      const o = c.createOscillator(); o.type = "sine"; o.frequency.value = f * mult;
      const g = c.createGain();
      g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.08 / (i + 1), t + 0.005);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.7);
      o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.72);
    });
  });
};
RECIPES.rel_tongue_drum = (c, m, p) => {
  pulser(c, m, p, 2 + Math.random() * 1.8, (t) => {
    const tones = [165, 196, 220, 247, 261.63];
    const f = tones[Math.floor(Math.random() * tones.length)];
    const o = c.createOscillator(); o.type = "sine"; o.frequency.value = f;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.14, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 2.4);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 2.5);
  });
};

/* ---------- RITUAL (10) — new dark/witchy category ----------- */
RECIPES.rit_cauldron = (c, m, p) => {
  looper(c, m, p, "brown", 280, 0.18, 0.5);
  pulser(c, m, p, 0.4 + Math.random() * 0.3, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.setValueAtTime(420, t);
    o.frequency.exponentialRampToValueAtTime(140, t + 0.18);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.06, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.2);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.22);
  });
};
RECIPES.rit_seance = (c, m, p) => {
  drone(c, m, p, 70, 0.10); drone(c, m, p, 105, 0.04, 7);
  RECIPES.amb_breath(c, m, p);
};
RECIPES.rit_ouija = (c, m, p) => {
  pulser(c, m, p, 4 + Math.random() * 3, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 1.8, "brown");
    const f = c.createBiquadFilter(); f.type = "lowpass"; f.frequency.value = 600;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.18, t + 0.5);
    g.gain.linearRampToValueAtTime(0.0, t + 1.7);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 1.8);
  });
};
RECIPES.rit_demon_whisper = (c, m, p) => {
  drone(c, m, p, 50, 0.06);
  pulser(c, m, p, 3 + Math.random() * 2, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 1.8, "pink");
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 900; f.Q.value = 4;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.12, t + 0.3);
    g.gain.linearRampToValueAtTime(0.0, t + 1.6);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 1.7);
  });
};
RECIPES.rit_ghost_radio = (c, m, p) => {
  looper(c, m, p, "white", 2400, 0.18, 0.5);
  pulser(c, m, p, 2 + Math.random() * 2, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 1100 + Math.random() * 1800;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.08, t + 0.02);
    g.gain.linearRampToValueAtTime(0.0, t + 0.4);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.42);
  });
};
RECIPES.rit_lich_drone = (c, m, p) => { drone(c, m, p, 38, 0.22); drone(c, m, p, 57, 0.10, 4); drone(c, m, p, 76, 0.06, -3); };
RECIPES.rit_necro_chant = (c, m, p) => {
  drone(c, m, p, 82, 0.10); drone(c, m, p, 123, 0.06, 3);
  pulser(c, m, p, 3, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 1.5, "pink");
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 320; f.Q.value = 2;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.14, t + 0.4);
    g.gain.linearRampToValueAtTime(0.0, t + 1.4);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 1.5);
  });
};
RECIPES.rit_blood_pact = (c, m, p) => {
  drone(c, m, p, 45, 0.12);
  pulser(c, m, p, 1.2, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 80;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.20, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.32);
  });
};
RECIPES.rit_vampire_silk = (c, m, p) => {
  drone(c, m, p, 130, 0.06); drone(c, m, p, 195, 0.04, 6);
  looper(c, m, p, "pink", 3800, 0.06, 0.5);
};
RECIPES.rit_summoning = (c, m, p) => {
  drone(c, m, p, 65, 0.10);
  pulser(c, m, p, 3, (t) => {
    [110, 138.59, 164.81].forEach((f) => {
      const o = c.createOscillator(); o.type = "sawtooth"; o.frequency.value = f;
      const g = c.createGain();
      g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.05, t + 1.0);
      g.gain.linearRampToValueAtTime(0.0, t + 4.5);
      o.connect(g); g.connect(m); o.start(t); o.stop(t + 4.6);
    });
  });
};

/* ---------- URBAN (10) — new city ambience category ---------- */
RECIPES.urb_subway = (c, m, p) => {
  drone(c, m, p, 90, 0.18); drone(c, m, p, 130, 0.08, 5);
  looper(c, m, p, "brown", 600, 0.22, 0.4);
};
RECIPES.urb_traffic = (c, m, p) => looper(c, m, p, "brown", 380, 0.18, 0.45);
RECIPES.urb_neon_buzz = (c, m, p) => { drone(c, m, p, 120, 0.10); looper(c, m, p, "white", 5400, 0.06, 0.4); };
RECIPES.urb_fire_escape = (c, m, p) => {
  drone(c, m, p, 50, 0.06);
  pulser(c, m, p, 4 + Math.random() * 4, (t) => {
    const o = c.createOscillator(); o.type = "sawtooth"; o.frequency.setValueAtTime(220, t);
    o.frequency.exponentialRampToValueAtTime(130, t + 0.4);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.08, t + 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.5);
  });
};
RECIPES.urb_alley_drip = (c, m, p) => {
  pulser(c, m, p, 1.4 + Math.random() * 0.8, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.setValueAtTime(2200, t);
    o.frequency.exponentialRampToValueAtTime(880, t + 0.18);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.10, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.2);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.22);
  });
};
RECIPES.urb_payphone = (c, m, p) => {
  drone(c, m, p, 350, 0.10); drone(c, m, p, 440, 0.10);
};
RECIPES.urb_helicopter = (c, m, p) => {
  drone(c, m, p, 90, 0.12);
  pulser(c, m, p, 0.05, (t) => {
    const o = c.createOscillator(); o.type = "square"; o.frequency.value = 28;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.04, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.04);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.05);
  });
};
RECIPES.urb_sirens = (c, m, p) => {
  const o = c.createOscillator(); o.type = "sawtooth"; o.frequency.value = 800;
  const lfo = c.createOscillator(); lfo.frequency.value = 0.6;
  const lfoG = c.createGain(); lfoG.gain.value = 200;
  lfo.connect(lfoG); lfoG.connect(o.frequency);
  const g = c.createGain(); g.gain.value = 0.05;
  o.connect(g); g.connect(m); o.start(); lfo.start();
  p(o); p(lfo); p(lfoG); p(g);
};
RECIPES.urb_rain_city = (c, m, p) => {
  RECIPES.urb_traffic(c, m, p);
  RECIPES.amb_rain(c, m, p);
};
RECIPES.urb_train_yard = (c, m, p) => {
  drone(c, m, p, 60, 0.12);
  pulser(c, m, p, 7 + Math.random() * 6, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 1.2, "brown");
    const f = c.createBiquadFilter(); f.type = "lowpass"; f.frequency.value = 280;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.35, t + 0.4);
    g.gain.linearRampToValueAtTime(0.0, t + 1.1);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 1.2);
  });
};

/* ---------- MACHINE (8) — server rooms / hvac ---------------- */
RECIPES.mch_server_room = (c, m, p) => { drone(c, m, p, 120, 0.10); drone(c, m, p, 240, 0.06, 6); looper(c, m, p, "pink", 3800, 0.08, 0.4); };
RECIPES.mch_hvac = (c, m, p) => looper(c, m, p, "brown", 240, 0.24, 0.4);
RECIPES.mch_elevator = (c, m, p) => { drone(c, m, p, 60, 0.10); drone(c, m, p, 110, 0.05, 4); };
RECIPES.mch_film_projector = (c, m, p) => {
  drone(c, m, p, 60, 0.06);
  pulser(c, m, p, 0.041, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 0.012, "white");
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 1800; f.Q.value = 6;
    const g = c.createGain();
    g.gain.setValueAtTime(0.10, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.014);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 0.02);
  });
};
RECIPES.mch_factory = (c, m, p) => {
  drone(c, m, p, 75, 0.12);
  pulser(c, m, p, 0.9, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 0.08, "brown");
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 220; f.Q.value = 1.5;
    const g = c.createGain();
    g.gain.setValueAtTime(0.20, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.08);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 0.1);
  });
};
RECIPES.mch_conveyor = (c, m, p) => {
  drone(c, m, p, 80, 0.08);
  pulser(c, m, p, 0.25, (t) => {
    const o = c.createOscillator(); o.type = "square"; o.frequency.value = 60;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.05, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.08);
  });
};
RECIPES.mch_robot_arm = (c, m, p) => {
  pulser(c, m, p, 2.0, (t) => {
    const o = c.createOscillator(); o.type = "sawtooth"; o.frequency.setValueAtTime(80, t);
    o.frequency.linearRampToValueAtTime(180, t + 0.4); o.frequency.linearRampToValueAtTime(80, t + 0.8);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.06, t + 0.05);
    g.gain.linearRampToValueAtTime(0.0, t + 0.8);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.85);
  });
};
RECIPES.mch_steam_release = (c, m, p) => {
  drone(c, m, p, 60, 0.06);
  pulser(c, m, p, 4 + Math.random() * 4, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 1.8, "white");
    const f = c.createBiquadFilter(); f.type = "highpass"; f.frequency.value = 3000;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.22, t + 0.15);
    g.gain.linearRampToValueAtTime(0.0, t + 1.6);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 1.7);
  });
};

/* ---------- More UNDERWATER (4) ------------------------------ */
RECIPES.uw_deep_pressure = (c, m, p) => { drone(c, m, p, 26, 0.22); drone(c, m, p, 38, 0.10, 4); };
RECIPES.uw_kelp_forest = (c, m, p) => {
  looper(c, m, p, "pink", 600, 0.18, 0.5);
  RECIPES.uw_bubbles(c, m, p);
};
RECIPES.uw_dolphin = (c, m, p) => {
  drone(c, m, p, 60, 0.04);
  pulser(c, m, p, 3 + Math.random() * 3, (t) => {
    const o = c.createOscillator(); o.type = "sine"; o.frequency.setValueAtTime(2200, t);
    o.frequency.exponentialRampToValueAtTime(4400, t + 0.18);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.10, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.2);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 0.22);
  });
};
RECIPES.uw_ship_creak = (c, m, p) => {
  drone(c, m, p, 50, 0.10);
  pulser(c, m, p, 7 + Math.random() * 6, (t) => {
    const o = c.createOscillator(); o.type = "sawtooth"; o.frequency.setValueAtTime(140, t);
    o.frequency.exponentialRampToValueAtTime(70, t + 1.6);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.10, t + 0.4);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.7);
    o.connect(g); g.connect(m); o.start(t); o.stop(t + 1.8);
  });
};

/* ---------- VOICE (8) — chant / breath textures -------------- */
RECIPES.vox_breath_inhale = (c, m, p) => {
  pulser(c, m, p, 3.0, (t) => {
    const s = c.createBufferSource(); s.buffer = noiseBuffer(c, 3.5, "pink");
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 1100; f.Q.value = 1.0;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.20, t + 1.4);
    g.gain.linearRampToValueAtTime(0.0, t + 3.4);
    s.connect(f); f.connect(g); g.connect(m); s.start(t); s.stop(t + 3.5);
  });
};
RECIPES.vox_choir_om = (c, m, p) => { drone(c, m, p, 136.1, 0.16); drone(c, m, p, 272.2, 0.06, 5); drone(c, m, p, 408.3, 0.04, -4); };
RECIPES.vox_monks_low = (c, m, p) => { drone(c, m, p, 73, 0.14); drone(c, m, p, 110, 0.08, 3); };
RECIPES.vox_throat_singing = (c, m, p) => {
  drone(c, m, p, 80, 0.12); drone(c, m, p, 240, 0.06, 4);
  const o = c.createOscillator(); o.type = "sine"; o.frequency.value = 1200;
  const g = c.createGain(); g.gain.value = 0.04;
  o.connect(g); g.connect(m); o.start();
  p(o); p(g);
};
RECIPES.vox_gregorian = (c, m, p) => { drone(c, m, p, 196, 0.10); drone(c, m, p, 247, 0.07, 4); drone(c, m, p, 294, 0.05, -3); };
RECIPES.vox_humming = (c, m, p) => { drone(c, m, p, 220, 0.10); drone(c, m, p, 330, 0.05, 5); };
RECIPES.vox_baby_lullaby = (c, m, p) => {
  RECIPES.rel_lullaby(c, m, p);
  drone(c, m, p, 220, 0.05);
};
RECIPES.vox_ghost_voices = (c, m, p) => {
  looper(c, m, p, "pink", 700, 0.18, 0.6);
  drone(c, m, p, 65, 0.06);
};

const engine = new ToneEngine();
export default engine;
export { RECIPES };
